/**
 * Silhouette Shapes for Pixel Morph Animation
 *
 * Each shape is a 2D grid where 1 = pixel, 0 = empty
 * Designed for ~200x200 canvas with 4px pixel size (50x50 grid max)
 */

// Brand colors
const COLORS = {
  cyan: '#00D4FF',
  gold: '#FFD700',
  magenta: '#FF00FF',
  green: '#00FF88',
  orange: '#FF8800',
  purple: '#AA44FF',
};

export interface SilhouetteShape {
  name: string;
  grid: number[][];
  color: string;
}

// Helper to create a shape from a string template (easier to visualize)
function shapeFromTemplate(template: string, color: string, name: string): SilhouetteShape {
  const lines = template.trim().split('\n');
  const grid = lines.map(line =>
    line.split('').map(char => char === '#' ? 1 : 0)
  );
  return { name, grid, color };
}

// Humanoid Warrior (16x24 grid)
const HUMANOID = shapeFromTemplate(`
......####......
.....######.....
.....######.....
......####......
.......##.......
...##########...
..############..
..############..
.....####.......
.....####.......
......##........
.....####.......
....######......
....##..##......
....##..##......
...##....##.....
...##....##.....
..##......##....
..##......##....
`, COLORS.cyan, 'humanoid');

// Dragon (20x16 grid)
const DRAGON = shapeFromTemplate(`
....##..........##..
...####........####.
..######......######
.##....##....##....#
.#......######.....#
.#......####.......#
##......####......##
###....######....###
.################...
..####......####....
...##..####..##.....
...##..####..##.....
....##......##......
.....########.......
......######........
........##..........
`, COLORS.gold, 'dragon');

// Slime/Blob (16x12 grid)
const SLIME = shapeFromTemplate(`
.....######.....
...##########...
..############..
.##############.
################
################
################
.##############.
..############..
...##########...
.....######.....
......####......
`, COLORS.green, 'slime');

// Tank/Golem (18x16 grid)
const TANK = shapeFromTemplate(`
......######......
....##########....
...############...
..####......####..
..##..######..##..
..##############..
..##############..
##################
##################
##################
##################
..##..........##..
..##..........##..
####..........####
####..........####
.##............##.
`, COLORS.orange, 'tank');

// Bird/Phoenix (20x14 grid)
const BIRD = shapeFromTemplate(`
........####........
.......######.......
......##....##......
.....##......##.....
....##........##....
##############..##..
################..##
.##############...##
....##......##......
.....##....##.......
......##..##........
.......####.........
........##..........
........##..........
`, COLORS.magenta, 'bird');

// Spider (18x14 grid)
const SPIDER = shapeFromTemplate(`
##..............##
.##............##.
..##..........##..
...##..####..##...
....##########....
....##########....
...############...
...############...
....##########....
....##..##..##....
...##..##..##..##.
..##...##...##..##
.##....##....##...
##......##.....##.
`, COLORS.purple, 'spider');

// Knight/Armored (16x22 grid)
const KNIGHT = shapeFromTemplate(`
......####......
.....######.....
....########....
....##....##....
....########....
.....######.....
.....######.....
...##########...
..############..
.##############.
.##..######..##.
.....######.....
.....######.....
.....######.....
....########....
....##....##....
....##....##....
...##......##...
...##......##...
..##........##..
..##........##..
.##..........##.
`, COLORS.cyan, 'knight');

// Wizard (14x22 grid)
const WIZARD = shapeFromTemplate(`
......##......
.....####.....
....######....
...########...
....######....
....######....
.....####.....
......##......
....######....
...########...
...########...
..##########..
..##########..
...########...
...########...
....######....
....######....
.....####.....
.....####.....
....##..##....
....##..##....
...##....##...
`, COLORS.purple, 'wizard');

// All shapes for cycling
export const SILHOUETTE_SHAPES: SilhouetteShape[] = [
  HUMANOID,
  DRAGON,
  SLIME,
  TANK,
  BIRD,
  SPIDER,
  KNIGHT,
  WIZARD,
];

export default SILHOUETTE_SHAPES;
